import React from "react";
import { Logo } from "./Logo";
import { useT } from "../lib/i18n";
import { safeExternalUrl, SAFE_BLANK } from "../lib/security";

export default function Footer({ onTabChange }) {
  const { t } = useT();

  return (
    <footer
      className="relative overflow-hidden text-white"
      style={{ background: "linear-gradient(135deg, #1a1430 0%, #0d0720 100%)" }}
      data-testid="site-footer"
    >
      <div className="ph-container py-14 lg:py-20 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold tracking-tight">
              <span style={{ color: "#3a1f9c" }}>protect</span>
              <span style={{ color: "#ffffff" }}>hub</span>
            </span>
          </div>
          <p className="mt-5 max-w-md text-white/70 leading-relaxed">
            {t("footer.tagline")}.
          </p>
          <p className="mt-2 text-sm text-white/50">
            Ciberseguridad con consciencia. Tecnología con propósito. Ética por diseño.
          </p>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white/80">
            {t("footer.sectionsTitle")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            <li>
              <button
                onClick={() => onTabChange("enterprise")}
                className="hover:text-white transition-colors"
                data-testid="footer-link-enterprise"
              >
                {t("nav.enterprise")}
              </button>
            </li>
            <li>
              <button
                onClick={() => onTabChange("segura")}
                className="hover:text-white transition-colors"
                data-testid="footer-link-segura"
              >
                {t("nav.segura")}
              </button>
            </li>
            <li>
              <button
                onClick={() => onTabChange("contact")}
                className="hover:text-white transition-colors"
                data-testid="footer-link-contact"
              >
                {t("nav.contact")}
              </button>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white/80">
            {t("footer.contactTitle")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            <li>
              <a
                href={safeExternalUrl("mailto:hola@protect-hub.com")}
                className="hover:text-white transition-colors"
                data-testid="footer-email-link"
              >
                hola@protect-hub.com
              </a>
            </li>
            <li>
              <a
                href={safeExternalUrl("https://wa.me/525571515109")}
                {...SAFE_BLANK}
                className="hover:text-white transition-colors"
                data-testid="footer-whatsapp-link"
              >
                +52 55 7151 5109
              </a>
            </li>
          </ul>

          {/* Redes sociales */}
          <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
            <a
              href={safeExternalUrl("https://www.linkedin.com/in/protect_hub/")}
              {...SAFE_BLANK}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white/8 hover:bg-white/15 transition-colors"
              style={{ background: "rgba(255,255,255,0.08)" }}
              data-testid="footer-social-linkedin"
              aria-label="LinkedIn de Protect Hub"
              title="LinkedIn @protect_hub"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a
              href={safeExternalUrl("https://www.instagram.com/protect_hub/")}
              {...SAFE_BLANK}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
              style={{ background: "rgba(255,255,255,0.08)" }}
              data-testid="footer-social-instagram"
              aria-label="Instagram de Protect Hub"
              title="Instagram @protect_hub"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
          </div>
          <h4 className="mt-6 text-sm font-semibold uppercase tracking-wider text-white/80">
            {t("footer.legalTitle")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            <li>
              <button
                onClick={() => onTabChange("legal", "aviso-privacidad")}
                className="hover:text-white transition-colors text-left"
                data-testid="footer-legal-privacy"
              >
                {t("footer.legalPrivacy")}
              </button>
            </li>
            <li>
              <button
                onClick={() => onTabChange("legal", "terminos-servicio")}
                className="hover:text-white transition-colors text-left"
                data-testid="footer-legal-terms"
              >
                {t("footer.legalTerms")}
              </button>
            </li>
            <li>
              <button
                onClick={() => onTabChange("legal", "cumplimiento-lfpdppp")}
                className="hover:text-white transition-colors text-left"
                data-testid="footer-legal-lfpdppp"
              >
                {t("footer.legalLFPDPPP")}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="ph-container py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Protect Hub. {t("footer.rights")}</p>
          <p data-testid="footer-made-with">{t("footer.madeWith")} 🇲🇽 mx</p>
        </div>
      </div>
    </footer>
  );
}
